import { api } from "./api";

/**
 * The optional AI layer.
 *
 * Every call here is allowed to fail. `getStatus` never throws -- a system with
 * no AI configured, an unreachable provider and a network error all resolve to
 * the same "not available", and the UI hides its AI controls. Nothing in the
 * appraisal screens depends on any of this succeeding.
 */

export type AIOperation =
  | "draft"
  | "improve"
  | "make_professional"
  | "make_concise"
  | "expand"
  | "summarize"
  | "rewrite"
  | "generate_from_evidence"
  | "generate_strengths"
  | "generate_areas_for_improvement"
  | "generate_development_suggestions";

/** Where outside an appraisal the assistant is helping, and what it is about.
 *  The server reads the records itself, through the user's own permissions --
 *  the page only says which ones. */
export interface AIContext {
  key: "financial.account_description" | "financial.approval_note" | "financial.credit_note_reason" | "financial.refund_reason" | "financial.period_reopen_reason" | "financial.bill_description" | "financial.banking_note" | "communication.message" | "communication.announcement" | "staff.setup_description" | "staff.department_head_reason" | "curriculum.setup_description" | "timetable.request_note" | "library.note" | "curriculum.teaching_plan" | "medical.cancellation_reason" | "transport.note" | "inventory.note" | "hostel.note" | "sports.note" | "events.note" | "discipline.note" | "attendance.note" | "examination.note" | "curriculum.coverage_note" | "students.class_note" | "students.lifecycle_note" | "scholarship.note" | "students.parent_note" | "alumni.note" | "counseling.reason" | "cafeteria.note" | "uniforms.note" | "facility.note" | "meetings.note" | "emergency.note" | "financial.governance_note";
  params: Record<string, string | number | null | undefined>;
}

export type AIOutcome = "accepted" | "edited" | "rejected" | "abandoned";

export interface AIStatus {
  enabled: boolean;
  available: boolean;
  provider: string;
  model: string;
  can_assist: boolean;
  can_search: boolean;
}

export interface AISuggestion {
  interaction_id: number;
  operation: AIOperation;
  suggestion: string;
  model: string;
  provider: string;
  generated_at: string;
  requires_human_acceptance: true;
}

export interface AIAnswer {
  interaction_id: number;
  answer: string;
  tools_used: string[];
  model: string;
  provider: string;
}

export const AI_UNAVAILABLE: AIStatus = {
  enabled: false,
  available: false,
  provider: "disabled",
  model: "",
  can_assist: false,
  can_search: false,
};

/** The labels used on the assistance buttons, in the order they are shown. */
export const WRITING_ACTIONS: { operation: AIOperation; label: string }[] = [
  { operation: "generate_from_evidence", label: "Generate from evidence" },
  { operation: "improve", label: "Improve writing" },
  { operation: "make_professional", label: "Make professional" },
  { operation: "make_concise", label: "Make concise" },
  { operation: "expand", label: "Expand" },
  { operation: "summarize", label: "Summarise" },
];

/** Operations that work from ERP evidence rather than from typed text, so the
 * button stays available on an empty field. */
export const EVIDENCE_ACTIONS: AIOperation[] = [
  "generate_from_evidence",
  "generate_strengths",
  "generate_areas_for_improvement",
  "generate_development_suggestions",
];

export const aiService = {
  /**
   * Whether AI exists for this user. Resolves to AI_UNAVAILABLE rather than
   * rejecting, because "is there an assistant" is not a question the page
   * should be able to fail on.
   */
  getStatus: async (): Promise<AIStatus> => {
    try {
      const { data } = await api.get<AIStatus>("/ai/status/");
      return { ...AI_UNAVAILABLE, ...data };
    } catch {
      return AI_UNAVAILABLE;
    }
  },

  /** The same question as getStatus, but a failure is reported rather than
   * folded into "off" -- so a field can say the assistant is not answering
   * and offer to try again, instead of silently losing its buttons. */
  fetchStatus: async (): Promise<AIStatus> => {
    const { data } = await api.get<AIStatus>("/ai/status/");
    return { ...AI_UNAVAILABLE, ...data };
  },

  assist: async (
    operation: AIOperation,
    options: { text?: string; appraisal?: number; context?: AIContext } = {},
  ): Promise<AISuggestion> => {
    const { data } = await api.post<AISuggestion>("/ai/assist/", {
      operation,
      text: options.text ?? "",
      appraisal: options.appraisal,
      context: options.context?.key,
      params: options.context?.params,
    });
    return data;
  },

  ask: async (question: string): Promise<AIAnswer> => {
    const { data } = await api.post<AIAnswer>("/ai/ask/", { question });
    return data;
  },

  /**
   * Tell the server what the human decided. This is what makes the audit
   * trail meaningful, and it is also what clears the stored suggestion -- so
   * it is sent even on a rejection.
   */
  recordOutcome: async (interactionId: number, outcome: AIOutcome) => {
    try {
      await api.post(`/ai/interactions/${interactionId}/outcome/`, { outcome });
    } catch {
      // Recording the decision must never block the user from getting on with
      // the appraisal; the interaction simply stays pending.
    }
  },

  getTools: async () => {
    const { data } = await api.get<{ name: string; description: string }[]>(
      "/ai/tools/",
    );
    return data;
  },
};

/**
 * Turn an AI failure into something worth showing a person.
 *
 * A 503 is the assistant being off or unreachable, which is ordinary and not
 * the user's problem to solve.
 */
export function aiErrorMessage(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { detail?: string } } })
    ?.response;
  if (!response) return "The assistant could not be reached.";
  if (response.status === 503) {
    return response.data?.detail ?? "The assistant is not available right now.";
  }
  if (response.status === 429) {
    return "You have used the assistant a lot recently. Try again shortly.";
  }
  if (response.status === 403) {
    return "Your account cannot use the assistant here.";
  }
  return response.data?.detail ?? "The assistant could not complete that.";
}

export default aiService;
